import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { usePagination } from '../hooks/usePagination';
import { useDebounce } from '../hooks/useDebounce';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';
import { Plus, Trash2, Edit2, Users } from 'lucide-react';
import { toast } from 'sonner';

const CustomersPage = () => {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [formData, setFormData] = useState({
    name: '',
    document: '',
    phone: '',
    email: '',
    birthDate: '',
  });

  // Pagination state
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const pagination = usePagination({
    initialPage: 1,
    itemsPerPage: 10,
    totalItems: paginationData.totalItems,
  });

  useEffect(() => {
    loadCustomers();
  }, [pagination.currentPage, debouncedSearchTerm]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.getCustomers({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: debouncedSearchTerm,
      });
      
      if (response.data && response.pagination) {
        setCustomers(response.data);
        setPaginationData(response.pagination);
      } else {
        setCustomers(Array.isArray(response) ? response : []);
        setPaginationData({
          currentPage: 1,
          totalPages: 1,
          totalItems: Array.isArray(response) ? response.length : 0,
          itemsPerPage: 10,
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar clientes');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name,
        document: customer.document,
        phone: customer.phone,
        email: customer.email || '',
        birthDate: customer.birthDate ? new Date(customer.birthDate).toISOString().split('T')[0] : '',
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '',
        document: '',
        phone: '',
        email: '',
        birthDate: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        document: formData.document,
        phone: formData.phone,
        email: formData.email || undefined,
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : undefined,
      };

      if (editingCustomer) {
        await api.updateCustomer(editingCustomer.id, payload);
        toast.success('Cliente atualizado com sucesso');
      } else {
        await api.createCustomer(payload);
        toast.success('Cliente criado com sucesso');
      }

      setDialogOpen(false);
      setEditingCustomer(null);
      setFormData({
        name: '',
        document: '',
        phone: '',
        email: '',
        birthDate: '',
      });
      loadCustomers();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar cliente');
    }
  };

  const handleToggleEnabled = async (customer) => {
    try {
      await api.toggleCustomer(customer.id);
      toast.success(`Cliente ${!customer.enabled ? 'ativado' : 'desativado'} com sucesso`);
      loadCustomers();
    } catch (error) {
      toast.error('Erro ao alterar status do cliente');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este cliente?')) return;
    try {
      await api.deleteCustomer(id);
      toast.success('Cliente deletado');
      loadCustomers();
    } catch (error) {
      toast.error('Erro ao deletar cliente');
    }
  };

  const handlePageChange = (page) => {
    pagination.goToPage(page);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Clientes</h1>
          <p className="text-muted-foreground mt-1">Gerencie o cadastro de clientes</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="add-customer-button">
                <Plus className="w-4 h-4 mr-2" />
                Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingCustomer ? 'Editar Cliente' : 'Cadastrar Novo Cliente'}</DialogTitle>
                <DialogDescription>
                  {editingCustomer ? 'Atualize os dados do cliente' : 'Preencha os dados do cliente'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="document">Documento *</Label>
                  <Input
                    id="document"
                    value={formData.document}
                    onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birthDate">Data de Nascimento</Label>
                  <Input
                    id="birthDate"
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full">
                  {editingCustomer ? 'Atualizar Cliente' : 'Cadastrar Cliente'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search Input */}
      <SearchInput
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Pesquisar por nome, documento ou telefone..."
        searchFields={['Nome', 'Documento', 'Telefone']}
      />

      {/* Customers Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Nome</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Documento</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Telefone</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr
                  key={customer.id}
                  className="border-b hover:bg-muted/50 transition-colors"
                  data-testid={`customer-row-${customer.id}`}
                >
                  <td className="px-6 py-4 text-sm font-medium">{customer.name}</td>
                  <td className="px-6 py-4 text-sm font-mono">{customer.document}</td>
                  <td className="px-6 py-4 text-sm">{customer.phone}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {customer.email || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    {isAdmin() ? (
                      <Switch
                        checked={customer.enabled}
                        onCheckedChange={() => handleToggleEnabled(customer)}
                        aria-label={`${customer.enabled ? 'Desativar' : 'Ativar'} cliente`}
                      />
                    ) : (
                      <Badge variant={customer.enabled ? 'default' : 'secondary'}>
                        {customer.enabled ? 'Ativo' : 'Inativo'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    {isAdmin() && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(customer)}
                          data-testid={`edit-customer-${customer.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(customer.id)}
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {paginationData.totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => handlePageChange(paginationData.currentPage - 1)}
                className={paginationData.currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            
            {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => handlePageChange(page)}
                  isActive={page === paginationData.currentPage}
                  className="cursor-pointer"
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => handlePageChange(paginationData.currentPage + 1)}
                className={paginationData.currentPage === paginationData.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {customers.length === 0 && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {searchTerm ? 'Nenhum cliente encontrado.' : 'Nenhum cliente encontrado.'}
              {isAdmin() && !searchTerm && ' Cadastre o primeiro!'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomersPage;
